import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, extractApiError } from "../lib/api";
import Layout from "../components/Layout";
import { ArrowLeft, CheckCircle, PencilSimple, Trash, Globe } from "@phosphor-icons/react";
import CustomExerciseModal from "../components/CustomExerciseModal";
import { useAuth } from "../contexts/AuthContext";

export default function ExerciseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ex, setEx] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = () =>
    api.get(`/exercises/${id}`).then((r) => setEx(r.data)).catch(() => setEx(false));
  useEffect(() => { load(); }, [id]);

  if (ex === null) return <Layout><div className="label-mono animate-pulse">LOADING...</div></Layout>;
  if (ex === false) return <Layout><div>Exercise not found.</div></Layout>;

  const isOwner = user && ex.custom && ex.created_by === user.id;
  const isAdmin = user?.role === "admin";
  const canManage = isOwner || isAdmin;

  const promote = async () => {
    if (!window.confirm(
      ex.is_global
        ? "Remove this drill from the global library? (Your personal copy stays.)"
        : "Promote this drill to the global library so every operator can use it?"
    )) return;
    try {
      const { data } = await api.post(`/exercises/${ex.id}/promote`);
      setEx({ ...ex, is_global: data.is_global });
    } catch (e) {
      alert("Promote failed: " + extractApiError(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this custom drill permanently? This can't be undone.")) return;
    try {
      await api.delete(`/exercises/${ex.id}`);
      navigate("/library");
    } catch (e) {
      alert("Delete failed: " + extractApiError(e));
    }
  };

  return (
    <Layout>
      <Link to="/library" className="inline-flex items-center gap-2 label-mono hover:text-volt mb-6" data-testid="exercise-back-link">
        <ArrowLeft size={14} weight="bold" /> Back to Library
      </Link>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="border border-border bg-card overflow-hidden relative">
          {ex.custom && (
            <div className="absolute top-3 right-3 z-10 bg-volt text-black px-3 py-1 text-xs font-display uppercase tracking-widest font-black" data-testid="custom-badge">
              {ex.is_global ? "GLOBAL CUSTOM" : "MY CUSTOM"}
            </div>
          )}
          {ex.image_url ? (
            <img
              src={ex.image_url}
              alt={ex.name}
              className="w-full aspect-[4/3] object-cover"
              onError={(e) => { e.currentTarget.src = "/icon-512.png"; e.currentTarget.classList.add("object-contain","p-16","opacity-60"); e.currentTarget.classList.remove("object-cover"); }}
            />
          ) : (
            <img src="/icon-512.png" alt={ex.name} className="w-full aspect-[4/3] object-contain p-16 opacity-60" />
          )}
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2 py-1 bg-volt text-black label-mono">{ex.domain}</span>
            <span className="px-2 py-1 border border-border label-mono">{ex.level}</span>
            <span className="px-2 py-1 border border-border label-mono">{ex.equipment}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black leading-none mb-3">{ex.name}</h1>
          <div className="text-sm text-muted-foreground mb-6">
            Targets: {(ex.muscle_groups || [ex.body_part]).filter(Boolean).join(" · ")}
          </div>

          {/* Manage row — only owner/admin for custom drills */}
          {canManage && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="custom-manage-row">
              {isOwner && (
                <button
                  onClick={() => setEditing(true)}
                  className="border border-border px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:border-volt hover:text-volt"
                  data-testid="edit-custom-btn"
                >
                  <PencilSimple size={12} weight="bold" /> Edit
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={promote}
                  className={`border px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-1 ${
                    ex.is_global
                      ? "border-volt text-volt"
                      : "border-border hover:border-volt hover:text-volt"
                  }`}
                  data-testid="promote-custom-btn"
                >
                  <Globe size={12} weight="bold" /> {ex.is_global ? "Unmake Global" : "Promote to Global"}
                </button>
              )}
              <button
                onClick={remove}
                className="border border-border px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:border-destructive hover:text-destructive"
                data-testid="delete-custom-btn"
              >
                <Trash size={12} weight="bold" /> Delete
              </button>
            </div>
          )}

          <div className="mb-8">
            <div className="label-mono mb-3">// EXECUTION CUES</div>
            <ol className="space-y-3">
              {(ex.instructions || []).map((line, i) => (
                <li key={`${i}-${line.slice(0, 24)}`} className="flex gap-3">
                  <CheckCircle size={20} weight="fill" className="text-volt shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ol>
          </div>

          {ex.cues && ex.cues.length > 0 && (
            <div className="mb-8">
              <div className="label-mono mb-3">// QUICK CUES</div>
              <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                {ex.cues.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}

          {ex.notes && (
            <div className="mb-8">
              <div className="label-mono mb-3">// NOTES</div>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{ex.notes}</p>
            </div>
          )}
        </div>
      </div>

      {ex.alternate_details?.length > 0 && (
        <div className="mt-12">
          <div className="label-mono mb-3">// ALTERNATES</div>
          <h2 className="font-display text-3xl font-black mb-6">SWAP BY SKILL OR KIT</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ex.alternate_details.map((a) => (
              <Link to={`/exercise/${a.id}`} key={a.id} className="tactical-card overflow-hidden group" data-testid={`alt-card-${a.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={a.image_url || "/icon-512.png"} alt={a.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="font-display font-black uppercase">{a.name}</div>
                  <div className="label-mono text-muted-foreground">{a.level} · {a.equipment}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <CustomExerciseModal
          initial={ex}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setEditing(false);
            setEx({ ...ex, ...updated });
          }}
        />
      )}
    </Layout>
  );
}
